import { create } from 'zustand';
import {
  CandidateSource,
  SourcedCandidateStatus
} from 'generated-graphql/types';
import {
  mapFiltersToSearchPeoplePayload,
  mapSearchGeneratePayloadToFilters
} from 'modules/sourcing-external/utils/mapSearchPeoplePayload';
import { createApolloClient } from 'core/services/apollo/createApolloClient';
import {
  CreateSourcingCandidateDocument,
  DeleteSourcingCandidateDocument,
  GenerateSearchPeopleQueryFieldsDocument,
  UpdateSourcingCandidateDocument
} from 'generated-graphql/graphql';
import { SourcedCandidateQueryItem } from 'modules/sourcing-external/types/SourcedCandidateQueryItem';
import { fetchAllSourcedCandidates } from 'modules/sourcing-shared/utils/fetchAllSourcedCandidates';
import { splitSourcedCandidates } from 'modules/sourcing-external/utils/splitSourcedCandidates';
import { SourcingExternalStore } from 'modules/sourcing-external/stores/types';
import { initialSourcingExternalStoreValues } from 'modules/sourcing-external/stores/constants';
import { handleSourcingSharedSelectedCandidatesNextController } from 'modules/sourcing-shared/controllers/store-controllers/handleSourcingSharedSelectedCandidatesNextController';
import { handleSourcingSharedChooseChannelNextController } from 'modules/sourcing-shared/controllers/store-controllers/handleSourcingSharedChooseChannelNextController';
import { createSharedSourcingStoreMethods } from 'modules/sourcing-shared/shared-stores';
import { scrollToTop } from 'common/utils/scrollToTop';

export const useSourcingExternalStore = create<SourcingExternalStore>(
  (set, get) => ({
    ...initialSourcingExternalStoreValues,
    onInit: async (candidateBatch) => {
      set({
        ...initialSourcingExternalStoreValues,
        candidateBatch
      });
      // Generate search query fields in parallel to speed up the process
      // Note: Template generation now happens after channel selection
      get().onGenerateSearchQueryFields(candidateBatch);
    },
    onGenerateSearchQueryFields: async (candidateBatch) => {
      const client = createApolloClient();

      // First get the search query input
      const { data } = await client.mutate({
        mutation: GenerateSearchPeopleQueryFieldsDocument,
        variables: {
          input: {
            candidateBatchId: candidateBatch.id,
            jobDescription: candidateBatch.jobDescription
          }
        }
      });

      // If the search query input is not found, we use the job description parsed to generate a default search query input
      const fallbackSearchQueryInput = {
        job_title: [candidateBatch.jobDescriptionParsed?.jobTitle || ''],
        location: [candidateBatch.jobDescriptionParsed?.location || '']
      };

      const searchQueryInput = mapSearchGeneratePayloadToFilters(
        data?.generateSearchPeopleQueryFields || fallbackSearchQueryInput
      );

      // Now check if there are any sourced candidates
      const sourcedCandidates = (
        await fetchAllSourcedCandidates({
          client,
          candidateBatchId: candidateBatch.id
        })
      ).filter(
        (candidate) => candidate !== null
      ) as SourcedCandidateQueryItem[];

      const {
        awaitingEnrichmentSourcedCandidates,
        alreadyInvitedSourcedCandidates
      } = splitSourcedCandidates(sourcedCandidates);

      const newStep =
        awaitingEnrichmentSourcedCandidates.length > 0
          ? 'SELECTED_CANDIDATES'
          : 'SEARCH';

      set({
        candidateBatch,
        step: newStep,
        searchQueryInput,
        selectedRows: awaitingEnrichmentSourcedCandidates,
        alreadyInvitedRows: alreadyInvitedSourcedCandidates
      });

      scrollToTop();
    },
    onChangePage: (page) => {
      const candidateBatchId = get().candidateBatch?.id;
      if (!candidateBatchId) return;

      set((state) => ({
        searchQueryInput: {
          ...state.searchQueryInput,
          candidateBatchId,
          page
        }
      }));
    },
    updateSearchFilters: (filters) => {
      const candidateBatchId = get().candidateBatch?.id;
      if (!candidateBatchId) return;

      set((state) => ({
        searchQueryInput: {
          ...mapFiltersToSearchPeoplePayload(filters),
          id: state.searchQueryInput?.id,
          candidateBatchId
        }
      }));
    },
    updateSearchQueryId: (id) => {
      const candidateBatchId = get().candidateBatch?.id;
      if (!candidateBatchId) return;

      set((state) => ({
        searchQueryInput: {
          ...state.searchQueryInput,
          candidateBatchId,
          id
        }
      }));
    },
    onSelectRow: async (candidateBatch, row) => {
      // NOTE: If we don't have the fullName or title, we can't create a sourced candidate
      if (!row.fullName || !row.title) return;

      const linkedInUrl = row.linkedInProfileUrl;

      // Prevent duplicate selections
      if (get().pendingSelections.has(linkedInUrl)) return;

      // Mark as pending
      set((state) => ({
        pendingSelections: new Set([...state.pendingSelections, linkedInUrl])
      }));

      try {
        const client = createApolloClient();
        const { data } = await client.mutate({
          mutation: CreateSourcingCandidateDocument,
          variables: {
            input: {
              candidateBatchId: candidateBatch.id,
              contactOutRawData: JSON.stringify(row),
              fullName: row.fullName,
              linkedInProfileUrl: row.linkedInProfileUrl,
              organizationId: candidateBatch.organizationId,
              status: SourcedCandidateStatus.AwaitingEnrichment,
              title: row.title,
              location: row.location,
              skills: row.skills,
              education: row.education,
              experience: row.experience,
              source: CandidateSource.ExternallySourcedViaContactOut
            }
          }
        });

        if (data?.createSourcedCandidate) {
          const newRow =
            data.createSourcedCandidate as SourcedCandidateQueryItem;
          set((state) => ({
            selectedRows: [...state.selectedRows, newRow],
            selectedCustomizeCandidateId:
              state.selectedCustomizeCandidateId || newRow.id
          }));
        }
      } finally {
        // Remove from pending
        set((state) => {
          const newPending = new Set(state.pendingSelections);
          newPending.delete(linkedInUrl);
          return { pendingSelections: newPending };
        });
      }
    },
    onRemoveSelectedRow: async (selectedRow) => {
      const client = createApolloClient();
      const { data } = await client.mutate({
        mutation: DeleteSourcingCandidateDocument,
        variables: {
          input: {
            id: selectedRow.id
          }
        }
      });

      if (data?.deleteSourcedCandidate) {
        return set((state) => {
          const newSelectedRows = state.selectedRows.filter(
            (row) => row.id !== selectedRow.id
          );
          const newSelectedCandidateId =
            state.selectedCustomizeCandidateId === selectedRow.id
              ? newSelectedRows[0]?.id || null
              : state.selectedCustomizeCandidateId;

          if (
            (state.step === 'SELECTED_CANDIDATES' ||
              state.step === 'CHOOSE_CHANNEL' ||
              state.step === 'SETUP_APPLICATION' ||
              state.step === 'CUSTOMIZE_MESSAGE') &&
            newSelectedRows.length === 0
          ) {
            // Scroll to top when changing step
            scrollToTop();
            return {
              step: 'SEARCH',
              selectedRows: [],
              selectedCustomizeCandidateId: null
            };
          }
          return {
            selectedRows: newSelectedRows,
            selectedCustomizeCandidateId: newSelectedCandidateId
          };
        });
      }
    },
    onSelectMultipleRows: async ({ selectedRows, rows }) => {
      const candidateBatch = get().candidateBatch;

      if (!candidateBatch) return;

      const client = createApolloClient();

      const filteredRows = rows.filter(
        (row) =>
          !selectedRows.find(
            (selectedRow) =>
              row.linkedInProfileUrl &&
              selectedRow.linkedInProfileUrl === row.linkedInProfileUrl
          )
      );

      const promises = filteredRows.map(async (row) => {
        if (!row.fullName || !row.title) return;
        const { data } = await client.mutate({
          mutation: CreateSourcingCandidateDocument,
          variables: {
            input: {
              candidateBatchId: candidateBatch.id,
              contactOutRawData: JSON.stringify(row),
              fullName: row.fullName,
              linkedInProfileUrl: row.linkedInProfileUrl,
              organizationId: candidateBatch.organizationId,
              status: SourcedCandidateStatus.AwaitingEnrichment,
              source: CandidateSource.ExternallySourcedViaContactOut,
              title: row.title,
              location: row.location,
              skills: row.skills
            }
          }
        });
        if (data?.createSourcedCandidate) {
          return data.createSourcedCandidate;
        }
        return null;
      });

      const results = await Promise.all(promises);

      return set((state) => {
        const newSelectedRows = results.filter(
          (result) => result !== null
        ) as SourcedCandidateQueryItem[];
        const allSelectedRows = [...state.selectedRows, ...newSelectedRows];
        return {
          selectedRows: allSelectedRows,
          selectedCustomizeCandidateId:
            state.selectedCustomizeCandidateId || allSelectedRows[0]?.id || null
        };
      });
    },
    onDeselectAllRows: async (rows) => {
      const client = createApolloClient();

      const promises = rows.map(async (row) => {
        if (!row.fullName || !row.title) return;
        const { data } = await client.mutate({
          mutation: DeleteSourcingCandidateDocument,
          variables: {
            input: {
              id: row.id
            }
          }
        });
        if (data?.deleteSourcedCandidate) {
          return data.deleteSourcedCandidate;
        }
        return null;
      });

      await Promise.all(promises);
      return set({ selectedRows: [], selectedCustomizeCandidateId: null });
    },
    onInviteCandidates: async () => {
      scrollToTop();
      set({
        step: 'SELECTED_CANDIDATES'
      });
    },
    onInviteCandidateBack: () => {
      scrollToTop();
      set({ step: 'SEARCH' });
    },
    onSelectedCandidatesNext: (props) =>
      handleSourcingSharedSelectedCandidatesNextController({
        ...props,
        get,
        set
      }),
    onSelectedCandidatesBack: () => {
      scrollToTop();
      set({ step: 'SEARCH' });
    },
    ...createSharedSourcingStoreMethods({
      get,
      set
    }),
    onChooseChannelNext: async ({
      formData,
      shouldClearCustomInvitationMessages
    }) => {
      if (shouldClearCustomInvitationMessages) {
        const client = createApolloClient();
        const { selectedRows } = get();

        set({
          defaultStartingTemplatedMessage: null,
          customizeMessageFormData: null,
          selectedRows: selectedRows.map((row) => ({
            ...row,
            customInvitationMessage: null,
            candidateRelevance: null
          }))
        });

        // Update the backend to clear custom invitation messages and relevance
        const promises = selectedRows.map((row) =>
          client.mutate({
            mutation: UpdateSourcingCandidateDocument,
            variables: {
              input: {
                id: row.id,
                customInvitationMessage: null,
                candidateRelevance: null
              }
            }
          })
        );

        await Promise.all(promises);
      }

      await handleSourcingSharedChooseChannelNextController({
        channelFormData: formData,
        get,
        set
      });
    },
    updateSelectedRowCustomMessage: (candidateId, customMessage) => {
      set((state) => ({
        selectedRows: state.selectedRows.map((row) =>
          row.id === candidateId
            ? { ...row, customInvitationMessage: customMessage }
            : row
        )
      }));
    },
    setStep: (step) => {
      scrollToTop();
      set({ step });
    }
  })
);
